// What voice SAYS about a search must agree with what she SEES — issue #1690.
//
// ── The bug, reported from real use (Kasandra, Sep 22 2026) ──────────────────
//
// She asked voice to look something up. The result links appeared in her chat
// while Kin's voice was saying she couldn't find anything on the web. Both
// halves reached her, and they contradicted each other.
//
// The cause is that the two halves have never had a common source of truth.
// `kin-search` builds them independently (kin-search/index.ts):
//
//   spoken = Claude's PROSE, the text blocks joined
//   links  = URLs scraped out of the web_search_tool_result blocks
//
// `shapeSearchResponse` then shapes each one and never compares them. Its own
// SEARCH_SYSTEM prompt tells Claude "if the search didn't answer it, say so
// plainly in one sentence" — correct in isolation, and nothing there accounts
// for the fact that any pages it DID find are still on their way to her screen.
//
// ── Why the fix is here and not a prose check ────────────────────────────────
//
// The tempting fix is to detect "this spoken text sounds like a failure" and
// suppress one half. Two reasons not to:
//
//   1. Matching on prose is brittle in both directions. "I couldn't find her
//      exact size, but Target has three close ones" is a SUCCESS that contains
//      the failure phrase; "nothing doing" is a failure that contains none.
//   2. It fixes the symptom on the wrong side. The speaking model is not
//      lying — it genuinely does not know anything reached her screen. The tool
//      result it gets back is `{ ok: true, answer: spoken }` and nothing else.
//
// So this tells it. The link COUNT crosses back with the answer, plus the one
// rule that follows from it. A model that knows four results are already on her
// screen does not announce that it found nothing.
//
// ── The mirrored bug, fixed in the same pass ─────────────────────────────────
//
// The old client code returned early whenever `spoken` was empty, which DROPPED
// any links that came with it. So a search that found four good pages but whose
// summary got stripped to nothing (see stripForSpeech — it deliberately removes
// bare domains, and "Target.com has it for $12.99" becomes a subjectless
// fragment) delivered her nothing at all and claimed failure. Links with no
// usable summary are still a useful result: she gets them, and Kin says so.
//
// Pure and Deno-free, so the ordinary vitest suite covers it by relative import
// — same split as voiceSearchCap.ts and voiceCaptureGate.ts. useRealtimeVoice.ts
// owns the fetch and the posting; this owns the decision.

/** Seconds are the caller's problem; this only needs to know it ran out. */
export interface VoiceSearchOutcomeInput {
  /** The shaped spoken half from kin-search. May be "" — that is a real state. */
  spoken: string;
  /** How many links will be posted to her chat. Never negative. */
  linkCount: number;
  /** The client-side fetch aborted. Implies linkCount 0 — there was no body. */
  timedOut: boolean;
}

export interface VoiceSearchOutcome {
  /** The JSON object sent back as the function-call result. */
  toolResult: Record<string, unknown>;
  /** Whether the caller should append the links message to the chat thread. */
  postLinks: boolean;
}

/**
 * ⚠️ This is the sentence that fixes #1690. It is an instruction to the speaking
 * model, not copy she hears — the model phrases it in Kin's own voice.
 *
 * It must keep naming the count. "Some links are on screen" is weaker: the model
 * has been observed hedging into "I think I found something" when the quantity
 * is vague, which is its own kind of mismatch.
 */
export const LINKS_ON_SCREEN_RULE =
  "She can ALREADY SEE these results in her chat. Tell her what you found. NEVER say you found nothing, couldn't find it, or came up empty — the links are on her screen right now and that would contradict what she is looking at.";

/**
 * The inverse, and the reason this module handles the zero case explicitly
 * rather than just omitting the field: a model told nothing about links will
 * sometimes invite her to check her chat for links that were never sent.
 */
export const NO_LINKS_RULE =
  "No links reached her screen for this one. Do not tell her to check her chat, and do not promise her anything to tap.";

/**
 * Used when the search found pages but no speakable summary survived. She is
 * about to see real links, so "I found nothing" is flatly wrong; the honest
 * line is that there are results and no tidy summary.
 */
export const LINKS_WITHOUT_SUMMARY_ANSWER =
  "The search found results but no short summary came back.";

export const EMPTY_SEARCH_ERROR =
  "The search came back empty — nothing reached her screen either. Tell her you couldn't find it, and offer to keep going.";

export function decideVoiceSearchOutcome(
  input: VoiceSearchOutcomeInput,
): VoiceSearchOutcome {
  const spoken = typeof input.spoken === "string" ? input.spoken.trim() : "";
  // A non-finite or negative count must not become a truthy "we have links".
  const raw = Number(input.linkCount);
  const linkCount = Number.isFinite(raw) && raw > 0 ? Math.floor(raw) : 0;

  // ── Nothing at all reached her, by either channel ────────────────────────
  // The timeout branch stays distinct because its copy is different (the caller
  // supplies a "that took too long" line), and because a timeout is the one
  // failure where trying again is worth offering.
  if (linkCount === 0 && !spoken) {
    return {
      toolResult: {
        ok: false,
        error: input.timedOut ? "TIMED_OUT" : EMPTY_SEARCH_ERROR,
        links_on_screen: 0,
        guidance: NO_LINKS_RULE,
      },
      postLinks: false,
    };
  }

  // ── Links, but no speakable summary ─────────────────────────────────────
  // Previously this returned ok:false and threw the links away. That was the
  // mirrored half of #1690: she lost real results AND heard a false failure.
  if (linkCount > 0 && !spoken) {
    return {
      toolResult: {
        ok: true,
        answer: LINKS_WITHOUT_SUMMARY_ANSWER,
        links_on_screen: linkCount,
        guidance: LINKS_ON_SCREEN_RULE,
      },
      postLinks: true,
    };
  }

  // ── A summary, and links to go with it — the ordinary success ────────────
  if (linkCount > 0) {
    return {
      toolResult: {
        ok: true,
        answer: spoken,
        links_on_screen: linkCount,
        guidance: LINKS_ON_SCREEN_RULE,
      },
      postLinks: true,
    };
  }

  // ── A summary with no links ─────────────────────────────────────────────
  // Legitimate: a factual answer ("Target closes at 10") can come back with no
  // citation blocks. She hears the answer and is promised nothing to tap.
  return {
    toolResult: {
      ok: true,
      answer: spoken,
      links_on_screen: 0,
      guidance: NO_LINKS_RULE,
    },
    postLinks: false,
  };
}
